import { Link } from "react-router-dom";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { MediationView } from "@expertloop/shared";
import type { AdminSection } from "../admin-sections/useAdminSection";

/** Nombre de dossiers listés dans le bloc ; le reste se lit dans les discussions. */
export const MEDIATIONS_BLOCK_SIZE = 5;

export interface MediationsPreview {
	mediations: MediationView[];
	total: number;
}

interface MediationsBlockProps {
	section: AdminSection<MediationsPreview>;
}

/**
 * Bloc compact des médiations à régler.
 *
 * Il n'existe que lorsqu'il y a quelque chose à traiter : sans dossier ouvert,
 * en cours de chargement, en panne ou sans permission, la page d'accueil reste
 * identique à ce qu'elle était. Les dossiers les plus anciens passent d'abord.
 */
export function MediationsBlock({ section }: MediationsBlockProps) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();

	if (section.state.status !== "ready") {
		return null;
	}
	const { mediations, total } = section.state.data;
	if (total === 0) {
		return null;
	}

	const formatDate = (date: Date | string) =>
		new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" });

	return (
		<section className="admin-overview__section admin-overview__mediations" aria-label={t("adminOverview.mediationBlockTitle")}>
			<h2>
				{t("adminOverview.mediationBlockTitle")}
				<span className="admin-overview__attention-count">{total}</span>
			</h2>
			<ul className="admin-overview__attention">
				{mediations.map((mediation) => (
					<li key={mediation.order.id} className="admin-overview__attention-item">
						<span className="admin-overview__mediations-main">
							<strong>{mediation.product.name}</strong>
							<span className="admin-overview__mediations-meta">
								{formatCents(mediation.order.amount, mediation.order.currency)}
								{" · "}
								{t("adminOverview.mediationBlockSince", { date: formatDate(mediation.order.mediationRequestedAt) })}
							</span>
						</span>
						<Link className="admin-overview__attention-link" to={`/chats/${mediation.chat.id}`}>
							{t("adminOverview.mediationBlockOpen")}
						</Link>
					</li>
				))}
			</ul>
			{total > mediations.length && (
				<Link className="admin-overview__attention-link" to="/chats?mediation=pending">
					{t("adminOverview.mediationBlockMore", { count: total })}
				</Link>
			)}
		</section>
	);
}
